import { IconArrowDown, IconArrowsUpDown, IconArrowUp } from "@tabler/icons-react";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { CampaignColumnResizeHandle } from "@/components/campaign/CampaignColumnResizeHandle";
import {
  CAMPAIGN_NAME_STICKY_POSITION,
  CAMPAIGN_SWITCH_STICKY_POSITION,
  CAMPAIGN_TABLE_GRID,
  type CampaignTableColumnId,
  type CampaignTableColumnWidths,
} from "@/components/campaign/campaign-table-layout";
import type {
  CampaignTableSortDirection,
  CampaignTableSortKey,
} from "@/components/campaign/campaign-table-sort";
import { DataGridHeader } from "@/components/patterns/DataTable";
import { cn } from "@/lib/utils";

interface CampaignTableHeaderCellProps {
  columnId: CampaignTableColumnId;
  label: string;
  width: number;
  className?: string;
  style?: CSSProperties;
  sortDirection?: CampaignTableSortDirection;
  onSort?: (columnId: CampaignTableSortKey) => void;
  onResizeColumn: (columnId: CampaignTableColumnId, width: number) => void;
  onResetColumn: (columnId: CampaignTableColumnId) => void;
}

function CampaignTableHeaderCell({
  columnId,
  label,
  width,
  className,
  style,
  sortDirection,
  onSort,
  onResizeColumn,
  onResetColumn,
}: CampaignTableHeaderCellProps) {
  const { t } = useTranslation("campaign");
  const sortable = columnId === "impressions";
  return (
    <span className={cn("relative flex self-stretch items-center px-3.5", className)} style={style}>
      {sortable ? (
        <button
          type="button"
          className="flex min-w-0 items-center gap-1 truncate text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label={t("list.table.sortBy", { column: label })}
          aria-pressed={sortDirection != null}
          onClick={() => onSort?.("impressions")}
        >
          <span className="truncate">{label}</span>
          {sortDirection === "asc" ? (
            <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
          ) : sortDirection === "desc" ? (
            <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
          ) : (
            <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
          )}
        </button>
      ) : (
        <span className="truncate">{label}</span>
      )}
      <CampaignColumnResizeHandle
        columnId={columnId}
        width={width}
        ariaLabel={t("list.table.resizeColumnAria", { column: label })}
        onResize={onResizeColumn}
        onReset={onResetColumn}
      />
    </span>
  );
}

interface CampaignTableHeaderProps {
  columnWidths: CampaignTableColumnWidths;
  gridStyle: CSSProperties;
  visibleColumnIds: readonly CampaignTableColumnId[];
  sortKey: CampaignTableSortKey | null;
  sortDirection: CampaignTableSortDirection;
  onSort: (columnId: CampaignTableSortKey) => void;
  onResizeColumn: (columnId: CampaignTableColumnId, width: number) => void;
  onResetColumn: (columnId: CampaignTableColumnId) => void;
}

export function CampaignTableHeader({
  columnWidths,
  gridStyle,
  visibleColumnIds,
  sortKey,
  sortDirection,
  onSort,
  onResizeColumn,
  onResetColumn,
}: CampaignTableHeaderProps) {
  const { t } = useTranslation("campaign");
  const cell = (columnId: CampaignTableColumnId) => {
    const numericColumns: readonly CampaignTableColumnId[] = [
      "dailyBudget",
      "currentBudget",
      "clickCost",
      "orderCost",
      "ctr",
      "cvr",
      "acos",
      "roas",
      "spend",
      "impressions",
      "clicks",
      "orders",
      "units",
      "sales",
      "advertisedProductOrders",
      "latestRun",
    ];
    const className =
      columnId === "switch"
        ? cn(CAMPAIGN_SWITCH_STICKY_POSITION, "z-30 border-r border-line bg-subtle pl-3.5")
        : columnId === "campaign"
          ? cn(
              CAMPAIGN_NAME_STICKY_POSITION,
              "z-30 border-r border-line bg-subtle pl-3.5 pr-4 shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]",
            )
          : numericColumns.includes(columnId)
            ? "justify-end text-right"
            : undefined;
    return (
      <CampaignTableHeaderCell
        key={columnId}
        columnId={columnId}
        label={t(`list.table.${columnId}`)}
        width={columnWidths[columnId]}
        className={className}
        style={columnId === "campaign" ? { left: columnWidths.switch } : undefined}
        sortDirection={
          sortKey === "impressions" && columnId === "impressions" ? sortDirection : undefined
        }
        onSort={columnId === "impressions" ? onSort : undefined}
        onResizeColumn={onResizeColumn}
        onResetColumn={onResetColumn}
      />
    );
  };
  return (
    <DataGridHeader className={CAMPAIGN_TABLE_GRID} style={gridStyle}>
      {visibleColumnIds.map(cell)}
      <span />
    </DataGridHeader>
  );
}
